import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import type { AdminUser, Lead, PropertyType } from '../types';
import { PROPERTY_TYPE_LABELS } from '../types';
import { AdminLayout } from '../components/Layout';
import { RecordingPlayer } from '../components/RecordingPlayer';

export function AdminLeadDetail() {
    const { id } = useParams<{ id: string }>();
    const [admin, setAdmin] = useState<AdminUser | null>(null);
    const [lead, setLead] = useState<Lead | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [isEditing, setIsEditing] = useState(false);
    const [editForm, setEditForm] = useState<Partial<Lead>>({});
    const [actionLoading, setActionLoading] = useState(false);

    // Dedicated status update state
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [newStatus, setNewStatus] = useState<string>('');
    const [statusReason, setStatusReason] = useState('');

    const navigate = useNavigate();

    const getApiBase = () => {
        return '/api';
    };

    const request = async (endpoint: string, options: RequestInit = {}) => {
        const token = localStorage.getItem('rewagent_token');
        const response = await fetch(`${getApiBase()}${endpoint}`, {
            ...options,
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                ...options.headers,
            },
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Request failed');
        return data;
    };

    useEffect(() => {
        const stored = localStorage.getItem('rewagent_admin');
        if (stored) setAdmin(JSON.parse(stored));
        else navigate('/admin');
    }, [navigate]);

    useEffect(() => {
        if (admin && id) fetchLead();
    }, [admin, id]);

    const fetchLead = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await request(`/admin/leads/${id}`);
            setLead(data.lead);
            setEditForm(data.lead);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load lead');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = async () => {
        try { await request('/auth/logout', { method: 'POST' }); } catch { }
        localStorage.removeItem('rewagent_admin');
        localStorage.removeItem('rewagent_token');
        navigate('/admin');
    };

    const handleSaveEdit = async () => {
        setActionLoading(true);
        try {
            await request(`/admin/leads/${id}`, {
                method: 'PUT',
                body: JSON.stringify(editForm),
            });
            setIsEditing(false);
            fetchLead();
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to update lead');
        } finally {
            setActionLoading(false);
        }
    };

    const handleUpdateStatus = async () => {
        if (!newStatus || !lead) return;
        setActionLoading(true);
        try {
            await request(`/admin/leads/${lead.id}/status`, {
                method: 'PUT',
                body: JSON.stringify({ status: newStatus, reason: statusReason })
            });
            setShowStatusModal(false);
            fetchLead();
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to update status');
        } finally {
            setActionLoading(false);
            setNewStatus('');
            setStatusReason('');
        }
    };

    const formatPrice = (val: number | null | undefined) => val ? `$${Number(val).toLocaleString()}` : 'N/A';

    const getStatusBadgeClass = (status: string) => {
        switch (status) {
            case 'qualified': return 'bg-green-100 text-green-800 border-green-200';
            case 'disqualified': return 'bg-red-100 text-red-800 border-red-200';
            case 'new': return 'bg-[#f3ede3] text-[#1a1a1a] border-[#e3dbcf]';
            default: return 'bg-[#fcfbfa] text-[#6b635a] border-[#e3dbcf]';
        }
    };

    const getOccupancyLabel = (occ: number | null | undefined) => {
        if (occ === null || occ === undefined) return 'N/A';
        const labels = ['Vacant', 'Occupied by Owner', 'Occupied by Tenant', 'Unknown'];
        return labels[occ] || 'N/A';
    };

    if (!admin) return null;

    return (
        <AdminLayout
            activeTab="leads"
            onTabChange={(tab) => navigate(`/admin/${tab}`)}
            onLogout={handleLogout}
        >
            <div className="flex-1 w-full max-w-[90rem] mx-auto py-10 px-6">
                <Link
                    to="/admin/leads"
                    className="inline-flex items-center text-[#8c8578] hover:text-[#1a1a1a] mb-10 transition-colors uppercase tracking-widest text-xs font-bold"
                >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to Leads
                </Link>

                {loading ? (
                    <div className="flex items-center justify-center py-24">
                        <svg className="animate-spin h-10 w-10 text-[#1a1a1a]" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                    </div>
                ) : error ? (
                    <div className="text-center py-20 bg-[#f3ede3] border border-[#e3dbcf] rounded-2xl">
                        <p className="text-red-600 font-medium">{error}</p>
                    </div>
                ) : lead && (
                    <div className="bg-[#fcfbfa] rounded-2xl p-8 border border-[#e3dbcf] shadow-sm">
                        {/* Header Box */}
                        <div className="flex flex-col md:flex-row md:items-start justify-between gap-6 mb-8 pb-8 border-b border-[#e3dbcf]">
                            <div>
                                <p className="text-[#8c8578] font-bold tracking-widest text-xs uppercase mb-2">Lead ID: {lead.lead_code}</p>
                                <h1 className="text-3xl font-bold text-[#1a1a1a] mb-2">{lead.owner_firstname} {lead.owner_lastname}</h1>
                                <p className="text-[#6b635a]">{lead.prop_address}</p>
                            </div>
                            <div className="flex items-center gap-4">
                                <span className={`inline-flex px-3 py-1.5 rounded-md text-sm font-bold tracking-widest uppercase border ${getStatusBadgeClass(lead.status)}`}>
                                    {lead.status}
                                </span>
                                {!isEditing && (
                                    <>
                                        <button
                                            onClick={() => { setNewStatus(lead.status); setStatusReason(''); setShowStatusModal(true); }}
                                            className="px-5 py-2 bg-[#f3ede3] text-[#1a1a1a] border border-[#e3dbcf] hover:bg-[#e3dbcf] rounded-lg text-sm font-bold transition-all"
                                        >
                                            Update Status
                                        </button>
                                        <button
                                            onClick={() => setIsEditing(true)}
                                            className="px-5 py-2 bg-[#1a1a1a] text-[#f3ede3] hover:bg-[#000000] rounded-lg text-sm font-bold transition-all"
                                        >
                                            Edit Details
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>

                        {isEditing ? (
                            // EDIT MODE
                            <div className="space-y-8">
                                {/* Contact Edit */}
                                <div>
                                    <h3 className="text-xs font-bold text-[#8c8578] uppercase tracking-widest mb-4">Contact Details</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-xs font-semibold text-[#6b635a] mb-1 block">First Name</label>
                                            <input type="text" value={editForm.owner_firstname || ''} onChange={(e) => setEditForm({ ...editForm, owner_firstname: e.target.value })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg px-4 py-2 text-[#1a1a1a]" />
                                        </div>
                                        <div>
                                            <label className="text-xs font-semibold text-[#6b635a] mb-1 block">Last Name</label>
                                            <input type="text" value={editForm.owner_lastname || ''} onChange={(e) => setEditForm({ ...editForm, owner_lastname: e.target.value })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg px-4 py-2 text-[#1a1a1a]" />
                                        </div>
                                        <div>
                                            <label className="text-xs font-semibold text-[#6b635a] mb-1 block">Phone</label>
                                            <input type="text" value={editForm.owner_phone || ''} onChange={(e) => setEditForm({ ...editForm, owner_phone: e.target.value })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg px-4 py-2 text-[#1a1a1a]" />
                                        </div>
                                        <div>
                                            <label className="text-xs font-semibold text-[#6b635a] mb-1 block">Email</label>
                                            <input type="email" value={editForm.owner_email || ''} onChange={(e) => setEditForm({ ...editForm, owner_email: e.target.value })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg px-4 py-2 text-[#1a1a1a]" />
                                        </div>
                                    </div>
                                </div>

                                {/* Property Edit */}
                                <div>
                                    <h3 className="text-xs font-bold text-[#8c8578] uppercase tracking-widest mb-4">Property Address & Specs</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="md:col-span-2">
                                            <label className="text-xs font-semibold text-[#6b635a] mb-1 block">Address</label>
                                            <input type="text" value={editForm.prop_address || ''} onChange={(e) => setEditForm({ ...editForm, prop_address: e.target.value })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg px-4 py-2 text-[#1a1a1a]" />
                                        </div>
                                        <div>
                                            <label className="text-xs font-semibold text-[#6b635a] mb-1 block">Campaign</label>
                                            <input type="text" value={editForm.campaign_name || ''} onChange={(e) => setEditForm({ ...editForm, campaign_name: e.target.value })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg px-4 py-2 text-[#1a1a1a]" />
                                        </div>
                                        <div>
                                            <label className="text-xs font-semibold text-[#6b635a] mb-1 block">Property Type</label>
                                            <select value={editForm.prop_type || ''} onChange={(e) => setEditForm({ ...editForm, prop_type: e.target.value as PropertyType })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg px-4 py-2 text-[#1a1a1a]">
                                                {Object.entries(PROPERTY_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="text-xs font-semibold text-[#6b635a] mb-1 block">Occupancy</label>
                                            <select value={editForm.occupancy ?? 0} onChange={(e) => setEditForm({ ...editForm, occupancy: parseInt(e.target.value) as any })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg px-4 py-2 text-[#1a1a1a]">
                                                <option value={0}>Vacant</option>
                                                <option value={1}>Owner Occupied</option>
                                                <option value={2}>Tenant Occupied</option>
                                                <option value={3}>Unknown</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="text-xs font-semibold text-[#6b635a] mb-1 block">Bedrooms</label>
                                            <input type="number" step="0.5" value={editForm.bedrooms || ''} onChange={(e) => setEditForm({ ...editForm, bedrooms: parseFloat(e.target.value) })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg px-4 py-2 text-[#1a1a1a]" />
                                        </div>
                                        <div>
                                            <label className="text-xs font-semibold text-[#6b635a] mb-1 block">Bathrooms</label>
                                            <input type="number" step="0.5" value={editForm.bathrooms || ''} onChange={(e) => setEditForm({ ...editForm, bathrooms: parseFloat(e.target.value) })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg px-4 py-2 text-[#1a1a1a]" />
                                        </div>
                                        <div>
                                            <label className="text-xs font-semibold text-[#6b635a] mb-1 block">Land Size (acres)</label>
                                            <input type="number" step="0.01" value={editForm.land_size || ''} onChange={(e) => setEditForm({ ...editForm, land_size: parseFloat(e.target.value) })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg px-4 py-2 text-[#1a1a1a]" />
                                        </div>
                                        <div>
                                            <label className="text-xs font-semibold text-[#6b635a] mb-1 block">Land Utilities</label>
                                            <select value={editForm.land_utilities ?? ''} onChange={(e) => setEditForm({ ...editForm, land_utilities: e.target.value ? parseInt(e.target.value) : null })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg px-4 py-2 text-[#1a1a1a]">
                                                <option value="">N/A</option>
                                                <option value={0}>No Utilities</option>
                                                <option value={1}>Partial Utilities</option>
                                                <option value={2}>Full Utilities</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="text-xs font-semibold text-[#6b635a] mb-1 block">Property License</label>
                                            <select value={editForm.prop_license ?? 0} onChange={(e) => setEditForm({ ...editForm, prop_license: parseInt(e.target.value) as any })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg px-4 py-2 text-[#1a1a1a]">
                                                <option value={0}>No</option>
                                                <option value={1}>Yes</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="text-xs font-semibold text-[#6b635a] mb-1 block">Listing Status</label>
                                            <select value={editForm.listing ?? 0} onChange={(e) => setEditForm({ ...editForm, listing: parseInt(e.target.value) as any })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg px-4 py-2 text-[#1a1a1a]">
                                                <option value={0}>Not Listed</option>
                                                <option value={1}>Listed with Agent</option>
                                                <option value={2}>FSBO</option>
                                            </select>
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="text-xs font-semibold text-[#6b635a] mb-1 block">Updates / Improvements</label>
                                            <textarea value={editForm.updates_text || ''} onChange={(e) => setEditForm({ ...editForm, updates_text: e.target.value })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg px-4 py-2 text-[#1a1a1a] min-h-[80px]" placeholder="Any recent updates or improvements..." />
                                        </div>
                                    </div>
                                </div>

                                {/* Financials Edit */}
                                <div>
                                    <h3 className="text-xs font-bold text-[#8c8578] uppercase tracking-widest mb-4">Financials</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-xs font-semibold text-[#6b635a] mb-1 block">Market Value ($)</label>
                                            <input type="number" value={editForm.market_val || ''} onChange={(e) => setEditForm({ ...editForm, market_val: parseFloat(e.target.value) })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg px-4 py-2 text-[#1a1a1a]" />
                                        </div>
                                        <div>
                                            <label className="text-xs font-semibold text-[#6b635a] mb-1 block">Asking Price ($)</label>
                                            <input type="number" value={editForm.ask_price || ''} onChange={(e) => setEditForm({ ...editForm, ask_price: parseFloat(e.target.value) })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg px-4 py-2 text-[#1a1a1a]" />
                                        </div>
                                        <div>
                                            <label className="text-xs font-semibold text-[#6b635a] mb-1 block">Mortgage</label>
                                            <select value={editForm.mortgage ?? 0} onChange={(e) => setEditForm({ ...editForm, mortgage: parseInt(e.target.value) as any })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg px-4 py-2 text-[#1a1a1a]">
                                                <option value={0}>No Mortgage</option>
                                                <option value={1}>Has Mortgage</option>
                                                <option value={2}>Unknown</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="text-xs font-semibold text-[#6b635a] mb-1 block">Mortgage Amount ($)</label>
                                            <input type="number" value={editForm.mortgage_amount || ''} onChange={(e) => setEditForm({ ...editForm, mortgage_amount: parseFloat(e.target.value) })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg px-4 py-2 text-[#1a1a1a]" />
                                        </div>
                                    </div>
                                </div>

                                {/* Seller Details */}
                                <div>
                                    <h3 className="text-xs font-bold text-[#8c8578] uppercase tracking-widest mb-4">Seller Details</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-xs font-semibold text-[#6b635a] mb-1 block">Ownership Timeline</label>
                                            <select value={editForm.owshp_tmln ?? 0} onChange={(e) => setEditForm({ ...editForm, owshp_tmln: parseInt(e.target.value) })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg px-4 py-2 text-[#1a1a1a]">
                                                <option value={0}>Less than 1 year</option>
                                                <option value={1}>1-3 years</option>
                                                <option value={2}>3-5 years</option>
                                                <option value={3}>5-10 years</option>
                                                <option value={4}>10-20 years</option>
                                                <option value={5}>20+ years</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="text-xs font-semibold text-[#6b635a] mb-1 block">Sell Reason</label>
                                            <select value={editForm.sell_reason ?? 0} onChange={(e) => setEditForm({ ...editForm, sell_reason: parseInt(e.target.value) })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg px-4 py-2 text-[#1a1a1a]">
                                                <option value={0}>Downsizing</option>
                                                <option value={1}>Upgrading</option>
                                                <option value={2}>Relocating</option>
                                                <option value={3}>Financial Difficulty</option>
                                                <option value={4}>Inherited</option>
                                                <option value={5}>Divorce</option>
                                                <option value={6}>Retirement</option>
                                                <option value={7}>Tax Issues</option>
                                                <option value={8}>Tired Landlord</option>
                                                <option value={9}>Property Condition</option>
                                                <option value={10}>Market Timing</option>
                                                <option value={11}>Other</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="text-xs font-semibold text-[#6b635a] mb-1 block">Closing Timeline</label>
                                            <select value={editForm.closn_tmln ?? 0} onChange={(e) => setEditForm({ ...editForm, closn_tmln: parseInt(e.target.value) })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg px-4 py-2 text-[#1a1a1a]">
                                                <option value={0}>ASAP</option>
                                                <option value={1}>1-2 weeks</option>
                                                <option value={2}>2-4 weeks</option>
                                                <option value={3}>1-2 months</option>
                                                <option value={4}>2-3 months</option>
                                                <option value={5}>3-6 months</option>
                                                <option value={6}>6-12 months</option>
                                                <option value={7}>Not sure</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="text-xs font-semibold text-[#6b635a] mb-1 block">Motivation (1-10)</label>
                                            <input type="number" min="1" max="10" value={editForm.motivation || 5} onChange={(e) => setEditForm({ ...editForm, motivation: parseInt(e.target.value) })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg px-4 py-2 text-[#1a1a1a]" />
                                        </div>
                                    </div>
                                </div>

                                {/* Scoring & System */}
                                <div>
                                    <h3 className="text-xs font-bold text-[#8c8578] uppercase tracking-widest mb-4">Scoring & System</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-xs font-semibold text-[#6b635a] mb-1 block">Lead Score (1-10)</label>
                                            <input type="number" min="1" max="10" value={editForm.lead_score || 5} onChange={(e) => setEditForm({ ...editForm, lead_score: parseInt(e.target.value) })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg px-4 py-2 text-[#1a1a1a]" />
                                        </div>
                                        <div>
                                            <label className="text-xs font-semibold text-[#6b635a] mb-1 block">Condition Scale (1-10)</label>
                                            <input type="number" min="1" max="10" value={editForm.cond_scale || 5} onChange={(e) => setEditForm({ ...editForm, cond_scale: parseInt(e.target.value) })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg px-4 py-2 text-[#1a1a1a]" />
                                        </div>
                                        <div>
                                            <label className="text-xs font-semibold text-[#6b635a] mb-1 block">Lead Cost ($)</label>
                                            <input type="number" value={editForm.lead_cost || ''} onChange={(e) => setEditForm({ ...editForm, lead_cost: parseFloat(e.target.value) })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg px-4 py-2 text-[#1a1a1a]" />
                                        </div>
                                        <div>
                                            <label className="text-xs font-semibold text-[#6b635a] mb-1 block">Recording URL</label>
                                            <input type="text" value={editForm.recording_url || ''} onChange={(e) => setEditForm({ ...editForm, recording_url: e.target.value })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg px-4 py-2 text-[#1a1a1a]" placeholder="https://..." />
                                        </div>
                                    </div>
                                </div>

                                {/* Notes */}
                                <div>
                                    <h3 className="text-xs font-bold text-[#8c8578] uppercase tracking-widest mb-4">Notes</h3>
                                    <div className="grid grid-cols-1 gap-4">
                                        <div>
                                            <label className="text-xs font-semibold text-[#6b635a] mb-1 block">General Notes / Info</label>
                                            <textarea value={editForm.info || ''} onChange={(e) => setEditForm({ ...editForm, info: e.target.value })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg px-4 py-2 text-[#1a1a1a] min-h-[100px]" placeholder="General notes about this lead..." />
                                        </div>
                                        <div>
                                            <label className="text-xs font-semibold text-[#6b635a] mb-1 block">Lead Notes (Internal)</label>
                                            <textarea value={editForm.lead_notes || ''} onChange={(e) => setEditForm({ ...editForm, lead_notes: e.target.value })} className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg px-4 py-2 text-[#1a1a1a] min-h-[100px]" placeholder="Internal admin notes..." />
                                        </div>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="flex gap-4 pt-6 border-t border-[#e3dbcf]">
                                    <button onClick={handleSaveEdit} disabled={actionLoading} className="px-6 py-3 bg-[#1a1a1a] text-[#f3ede3] hover:bg-[#000000] rounded-lg font-bold">
                                        {actionLoading ? 'Saving...' : 'Save All Changes'}
                                    </button>
                                    <button onClick={() => { setIsEditing(false); setEditForm(lead); }} disabled={actionLoading} className="px-6 py-3 bg-[#fcfbfa] text-[#1a1a1a] border border-[#e3dbcf] hover:bg-[#f3ede3] rounded-lg font-medium">
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        ) : (
                            // READ ONLY MODE
                            <div className="flex flex-col lg:flex-row gap-16">
                                {/* Left Column: General Notes + Recording */}
                                <div className="lg:w-1/2 space-y-6">
                                    {lead.info && (
                                        <div>
                                            <h3 className="text-xs font-bold text-[#8c8578] uppercase tracking-widest mb-4">General Notes</h3>
                                            <div className="bg-[#f3ede3] rounded-xl p-6">
                                                <p className="text-[#1a1a1a] text-sm whitespace-pre-wrap">{lead.info}</p>
                                            </div>
                                        </div>
                                    )}
                                    {lead.recording_url && (
                                        <RecordingPlayer
                                            leadId={lead.id}
                                            apiBase={getApiBase()}
                                            routePrefix="/admin"
                                            authHeaders={{
                                                ...(localStorage.getItem('rewagent_token') ? { Authorization: `Bearer ${localStorage.getItem('rewagent_token')}` } : {}),
                                            }}
                                        />
                                    )}
                                </div>

                                {/* Right Column: Lead Details */}
                                <div className="lg:w-1/2">
                                    <div className="bg-[#fcfbfa] border border-[#e3dbcf] rounded-2xl p-8 md:p-10 shadow-sm">
                                        {/* Contact Details */}
                                        <div>
                                            <h3 className="text-xs font-bold text-[#8c8578] uppercase tracking-widest mb-6">Contact Details</h3>
                                            <div className="space-y-4">
                                                <div className="flex justify-between border-b border-[#f3ede3] pb-3">
                                                    <span className="text-[#6b635a] text-sm font-medium">Phone</span>
                                                    <span className="text-[#1a1a1a] font-bold">{lead.owner_phone || 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between pb-1">
                                                    <span className="text-[#6b635a] text-sm font-medium">Email</span>
                                                    <span className="text-[#1a1a1a] font-bold">{lead.owner_email || 'N/A'}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <hr className="my-8 border-[#e3dbcf]" />

                                        {/* Property Specs */}
                                        <div>
                                            <h3 className="text-xs font-bold text-[#8c8578] uppercase tracking-widest mb-6">Property Specs</h3>
                                            <div className="space-y-4">
                                                <div className="flex justify-between border-b border-[#f3ede3] pb-3">
                                                    <span className="text-[#6b635a] text-sm font-medium">Property Type</span>
                                                    <span className="text-[#1a1a1a] font-bold">{PROPERTY_TYPE_LABELS[lead.prop_type] || lead.prop_type}</span>
                                                </div>
                                                <div className="flex justify-between border-b border-[#f3ede3] pb-3">
                                                    <span className="text-[#6b635a] text-sm font-medium">Occupancy</span>
                                                    <span className="text-[#1a1a1a] font-bold">{getOccupancyLabel(lead.occupancy)}</span>
                                                </div>
                                                <div className="flex justify-between border-b border-[#f3ede3] pb-3">
                                                    <span className="text-[#6b635a] text-sm font-medium">Bedrooms</span>
                                                    <span className="text-[#1a1a1a] font-bold">{lead.bedrooms ?? 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between pb-1">
                                                    <span className="text-[#6b635a] text-sm font-medium">Bathrooms</span>
                                                    <span className="text-[#1a1a1a] font-bold">{lead.bathrooms ?? 'N/A'}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <hr className="my-8 border-[#e3dbcf]" />

                                        {/* System Data */}
                                        <div>
                                            <h3 className="text-xs font-bold text-[#8c8578] uppercase tracking-widest mb-6">System Data</h3>
                                            <div className="space-y-4">
                                                <div className="flex justify-between border-b border-[#f3ede3] pb-3">
                                                    <span className="text-[#6b635a] text-sm font-medium">Lead Score</span>
                                                    <span className="text-[#1a1a1a] font-bold">{lead.lead_score}/10</span>
                                                </div>
                                                <div className="flex justify-between border-b border-[#f3ede3] pb-3">
                                                    <span className="text-[#6b635a] text-sm font-medium">Lead Cost</span>
                                                    <span className="text-[#1a1a1a] font-bold">{formatPrice(lead.lead_cost)}</span>
                                                </div>
                                                <div className="flex justify-between border-b border-[#f3ede3] pb-3">
                                                    <span className="text-[#6b635a] text-sm font-medium">Submit Date</span>
                                                    <span className="text-[#1a1a1a] font-bold">{new Date(lead.created_at).toLocaleDateString()}</span>
                                                </div>
                                                <div className="flex justify-between pb-1">
                                                    <span className="text-[#6b635a] text-sm font-medium">Campaign</span>
                                                    <span className="text-[#1a1a1a] font-bold">{lead.campaign_name}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <hr className="my-8 border-[#e3dbcf]" />

                                        {/* Financials */}
                                        <div>
                                            <h3 className="text-xs font-bold text-[#8c8578] uppercase tracking-widest mb-6">Financials</h3>
                                            <div className="space-y-4">
                                                <div className="flex justify-between border-b border-[#f3ede3] pb-3">
                                                    <span className="text-[#6b635a] text-sm font-medium">Market Value</span>
                                                    <span className="text-[#1a1a1a] font-bold">{formatPrice(lead.market_val)}</span>
                                                </div>
                                                <div className="flex justify-between pb-1">
                                                    <span className="text-[#6b635a] text-sm font-medium">Asking Price</span>
                                                    <span className="text-[#1a1a1a] font-bold">{formatPrice(lead.ask_price)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Status Update Modal */}
            {showStatusModal && lead && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-[#fcfbfa] w-full max-w-md rounded-2xl shadow-xl overflow-hidden border border-[#e3dbcf]">
                        <div className="p-6 md:p-8">
                            <div className="flex justify-between items-start mb-2">
                                <h3 className="text-xl font-bold text-[#1a1a1a]">Update Status</h3>
                                <button onClick={() => setShowStatusModal(false)} className="text-[#8c8578] hover:text-[#1a1a1a] transition-colors">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>
                            <p className="text-sm text-[#6b635a] mb-6">Updating status for {lead.owner_firstname} {lead.owner_lastname}</p>

                            <div className="space-y-4">
                                <div>
                                    <label className="text-xs font-bold text-[#8c8578] uppercase tracking-widest mb-2 block">New Status</label>
                                    <select
                                        value={newStatus}
                                        onChange={(e) => setNewStatus(e.target.value)}
                                        className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-3 focus:outline-none focus:border-[#1a1a1a]"
                                    >
                                        <option value="new">New</option>
                                        <option value="qualified">Qualified</option>
                                        <option value="disqualified">Disqualified</option>
                                    </select>
                                </div>

                                {newStatus === 'disqualified' && (
                                    <div>
                                        <label className="text-xs font-bold text-[#8c8578] uppercase tracking-widest mb-2 block">Reason for Disqualification <span className="text-red-500">*</span></label>
                                        <textarea
                                            value={statusReason}
                                            onChange={(e) => setStatusReason(e.target.value)}
                                            placeholder="Enter reason..."
                                            className="w-full bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-3 min-h-[100px] focus:outline-none focus:border-[#1a1a1a]"
                                            required
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="mt-8 flex gap-3">
                                <button
                                    onClick={handleUpdateStatus}
                                    disabled={actionLoading || (newStatus === 'disqualified' && !statusReason.trim())}
                                    className="flex-1 bg-[#1a1a1a] text-[#f3ede3] px-4 py-3 rounded-lg font-bold text-sm disabled:opacity-50 transition-all hover:bg-[#000000]"
                                >
                                    {actionLoading ? 'Saving...' : 'Confirm Update'}
                                </button>
                                <button
                                    onClick={() => setShowStatusModal(false)}
                                    className="flex-1 bg-[#f3ede3] text-[#1a1a1a] border border-[#e3dbcf] px-4 py-3 rounded-lg font-bold text-sm transition-all hover:bg-[#e3dbcf]"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
}
